<template>
  <v-container
    class="pa-0"
    fluid
  >
    <v-row no-gutters>
      <v-col cols="12">
        <template v-for="(payment, index) in paymentData">
          <v-container
            :key="`payment-${payment.Timestamp}`"
            class="pa-0"
            fluid
          >
            <v-row dense>
              <!-- PAYMENT HEADER -->
              <v-col cols="12">
                <div
                  :class="[`${tableStaffColor(payment.StaffId)} lighten-4`]"
                  class="pa-1 grey--text text--darken-3 text-subtitle-1 font-weight-bold rounded"
                >
                  <v-container
                    class="pa-0"
                    fluid
                  >
                    <v-row
                      align="center"
                      dense
                      justify="center"
                    >
                      <v-col class="py-1 shrink text-no-wrap">
                        <div>
                          {{ $tc('Restaurant.Tables.Dialog.Payment.Title', 1) }} #{{ desc ? (paymentData.length - index) : (index + 1) }} -&nbsp;<span class="grey--text text--darken-1">{{
                            payment.Time
                          }}</span>
                        </div>

                        <div
                          v-if="payment.StaffName"
                          class="text-caption"
                        >
                          <div class="grey--text text--darken-2">
                            <strong>{{ $tc('Waiter.Title', 1) }}:</strong> {{ payment.StaffName }}
                          </div>
                        </div>
                      </v-col>

                      <v-col class="text-right">
                        <div class="d-inline-block">
                          <v-chip
                            v-if="getPaymentCouponsTotal(payment) > 0"
                            class="ml-1 mb-1 mb-sm-0"
                            color="purple"
                            label
                            text-color="white"
                          >
                            <v-icon left>
                              redeem
                            </v-icon>
                            {{ getPaymentCouponsTotal(payment) | currency }}
                          </v-chip>

                          <template v-for="(transaction, transIndex) in payment.Transactions">
                            <v-chip
                              :key="`payment-${payment.Timestamp}-transaction-${transIndex}`"
                              class="ml-1 mb-1 mb-sm-0"
                              color="blue-grey lighten-1"
                              label
                              text-color="white"
                            >
                              <v-icon left>
                                {{ getPaymentTypeByCode(transaction.PaymentType).icon }}
                              </v-icon>
                              {{ transaction.Amount | currency }}
                            </v-chip>
                          </template>
                        </div>

                        <div
                          v-if="$route.name === 'Tables'"
                          class="d-inline-block ml-1"
                        >
                          <v-tooltip
                            color="indigo"
                            max-width="285"
                            top
                          >
                            <template #activator="{on}">
                              <v-btn
                                :disabled="payment.loading"
                                :loading="payment.loading"
                                class="indigo white--text"
                                icon
                                small
                                @click="onRevertPaymentItems(payment)"
                                v-on="on"
                              >
                                <v-icon small>
                                  mdi-arrow-u-left-top
                                </v-icon>
                              </v-btn>
                            </template>

                            <span>{{ $t('Restaurant.Tables.Dialog.ViewTable.Dialog.ConfirmRevertPaymentItems.Tooltip') }}</span>
                          </v-tooltip>
                        </div>

                        <div
                          v-if="userCanViewFiscalDoc && $route.name === 'Tables'"
                          class="d-inline-block ml-1"
                        >
                          <issue-receipt-document-button
                            :badge-offset-x="15"
                            :badge-offset-y="10"
                            :can-cancel-document="posUserCan('RECEIPTS', 'MANAGE')"
                            :can-convert-receipt="appCanConvertReceiptToInvoice"
                            :can-issue-document="!paymentHasFiscalDocument(payment)"
                            :can-issue-receipt="!paymentHasFiscalDocument(payment)"
                            :disabled="generatePaymentReceiptLoading || payment.loading"
                            :document-id="paymentFiscalDocument(payment)?.OrderDocument?.Id"
                            :document-type="paymentFiscalDocument(payment)?.Document?.Type"
                            :document-url="paymentFiscalDocument(payment)?.OrderDocument?.DocumentUrl"
                            :from="EmailNotifyFromEnum.TABLE"
                            :icon-name="paymentHasFiscalDocument(payment) ? 'mdi-receipt-text-edit-outline' : 'mdi-receipt-text-plus-outline'"
                            :invoice="appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.INVOICE) && appHasInvoiceDocEnabled"
                            :label="paymentHasFiscalDocument(payment) ? $t('Common.Button.ManageDocument').toLocaleUpperCase($i18n.locale) : $t('Common.Button.IssueDocument').toLocaleUpperCase($i18n.locale)"
                            :receipt="appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.RECEIPT) && appHasReceiptDocEnabled"
                            :tooltip="paymentHasFiscalDocument(payment) ? paymentHasReceipt(payment) ? $t('Common.Button.ManageReceipt') : paymentHasInvoice(payment) ? $t('Common.Button.ManageInvoice') : $t('Common.Button.ManageFnb') : $t('Common.Button.IssueDocument')"
                            :tooltip-open-delay="150"
                            hide-label
                            icon
                            small
                            small-icon
                            small-size="28"
                            tooltip-position="top"
                            @view-document="data => $bus.$emit('fiscal-document:view', data.DocUrl)"
                            @issue-document="data => generatePaymentReceipt(payment, data.DocType)"
                            @cancel-document="data => $bus.$emit('fiscal-document:cancel', {ReceiptId: data.DocId, TableId: table?.Id}, data)"
                            @print-document="data => $bus.$emit('fiscal-document:print', data.DocId)"
                            @email-document="data => $bus.$emit('fiscal-document:email', data.DocId, {ReceiptId: data.DocId, TableId: table?.Id}, data)"
                            @convert-receipt="data => $bus.$emit('fiscal-document:convert-to-invoice', {ReceiptId: data.DocId, TableId: table?.Id}, data)"
                          />
                        </div>
                      </v-col>
                    </v-row>
                  </v-container>
                </div>
              </v-col>

              <!-- PAYMENT ITEMS -->
              <v-col cols="12">
                <v-container
                  class="mb-4 pa-0"
                  fluid
                >
                  <v-row>
                    <v-col cols="12">
                      <template v-for="tableSeat in getPaymentItems(payment)">
                        <v-subheader
                          :key="`payment-${payment.Timestamp}-table-seat-${tableSeat.Seat}`"
                          :class="[`${tableStaffColor(payment.StaffId)}`, isDarkMode ? 'darken-3' : 'lighten-5']"
                          class="font-weight-bold pl-1 rounded"
                          style="height: 26px;"
                        >
                          {{ tableSeat.Name }}
                        </v-subheader>

                        <template v-for="category in tableSeat.Items">
                          <v-subheader
                            :key="`payment-${payment.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}`"
                            class="grey--text text--darken-2 text-subtitle-1 font-weight-bold pl-0"
                            style="height: 36px;"
                          >
                            {{ getTranslatedField(getCategory(category.CategoryId), 'Name') || category.CategoryName }}
                          </v-subheader>

                          <v-list
                            :key="`payment-${payment.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-list`"
                            class="pa-0"
                            dense
                            three-line
                          >
                            <template v-for="(menu) in category.Items">
                              <v-list-item
                                :key="`payment-${payment.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-item-${menu.RowId}-hash-${menu.HashId}`"
                                :class="isDarkMode ? 'grey darken-4' : 'grey lighten-4'"
                                ripple
                              >
                                <v-list-item-content>
                                  <span
                                    :style="`background-color: #${getCategoryColor(category.CategoryId) || '333333'};`"
                                    style="position: absolute; width:5px; height: 100%; margin-left: -16px; padding: 0; margin-right: 16px; top: 1px;"
                                  />

                                  <v-list-item-title class="text-subtitle-1 font-weight-regular">
                                    <template v-if="menu.MenuCustomPrice && menu.UnitId > 1">
                                      <strong class="orange--text">{{ formatUnitQuantity($i18n.locale, menu.UnitQuantity, getMeasurementUnitById(menu.UnitId)) }}</strong>
                                    </template>
                                    <template v-else>
                                      <strong class="orange--text">
                                        {{ menu.PaymentQuantity }} x
                                      </strong>
                                    </template>
                                    {{ getTranslatedField(menu, 'Name') }}
                                  </v-list-item-title>

                                  <v-list-item-subtitle class="grey--text">
                                    <template v-if="menu.SpecialStatus && menu.IsSpecial && !menu.HasCustomPrice">
                                      <span class="old-price">
                                        {{ menu.MenuPrice | currency }}
                                      </span> {{ menu.SpecialPrice | currency }}
                                    </template>
                                    <template v-else>
                                      <template v-if="menu.MenuCustomPrice && menu.UnitId > 1">
                                        {{ menu.MenuPrice | currency }}
                                        <span class="grey--text text-caption">/ {{ getMeasurementUnitById(menu.UnitId).BaseUnit }}</span>
                                      </template>
                                      <template v-else>
                                        <span
                                          v-if="menuItemHasCoupon(menu)"
                                          class="old-price"
                                        >
                                          {{ parseFloat(menu.MenuPrice) | currency }}
                                        </span>
                                        {{ menuItemHasCoupon(menu) ? parseFloat(menu.PriceFmt) - (parseFloat(menu.Coupon.Amount) / parseInt(menu.PaymentQuantity)) : menu.PriceFmt | currency }}
                                      </template>
                                    </template>
                                  </v-list-item-subtitle>

                                  <v-list-item-subtitle class="pb-1 text-caption grey--text">
                                    {{ getMenuSelectedOptionsString(menu) }}&nbsp;
                                  </v-list-item-subtitle>
                                </v-list-item-content>

                                <v-list-item-action>
                                  <v-list-item-action-text class="text-subtitle-1 grey--text">
                                    <template v-if="menu.SpecialStatus && menu.IsSpecial && !menu.HasCustomPrice">
                                      <span class="old-price">
                                        <template v-if="menu.MenuCustomPrice && menu.UnitId > 1">
                                          {{ parseFloat(menu.MenuPrice) | currency }}
                                        </template>
                                        <template v-else>
                                          {{ parseFloat(parseFloat(menu.MenuPrice) * parseInt(menu.PaymentQuantity)) | currency }}
                                        </template>
                                      </span>
                                      {{ parseFloat(parseFloat(menu.SpecialPrice) * parseInt(menu.PaymentQuantity)) | currency }}
                                    </template>

                                    <template v-else>
                                      <span
                                        v-if="menuItemHasCoupon(menu)"
                                        class="old-price"
                                      >
                                        <template v-if="menu.MenuCustomPrice && menu.UnitId > 1">
                                          {{ parseFloat(menu.Price) | currency }}
                                        </template>
                                        <template v-else>
                                          {{ parseFloat(parseFloat(menu.Price) * parseInt(menu.PaymentQuantity)) | currency }}
                                        </template>
                                      </span>
                                      {{ menuItemPriceTotal(menu, menuItemHasCoupon(menu)) | currency }}
                                    </template>
                                  </v-list-item-action-text>

                                  <v-list-item-action-text>
                                    <v-btn
                                      v-if="menu.Custom && parseInt(menu.Custom.Order)"
                                      class="d-inline-block ml-1 px-1"
                                      color="pink lighten-1"
                                      ripple
                                      small
                                      style="min-width: 0px;"
                                      text
                                    >
                                      <v-icon>
                                        low_priority
                                      </v-icon>

                                      <span style="position: relative; top: -4px;">{{ menu.Custom.Order }}</span>
                                    </v-btn>

                                    <v-btn
                                      v-if="menu.Custom && parseInt(menu.Custom.Seat)"
                                      class="d-inline-block ml-1 px-1"
                                      color="brown lighten-1"
                                      small
                                      style="min-width: 0px;"
                                      text
                                    >
                                      <v-icon>
                                        event_seat
                                      </v-icon>

                                      <span style="position: relative; top: -4px;">{{ menu.Custom.Seat }}</span>
                                    </v-btn>

                                    <v-btn
                                      v-if="menuItemHasCoupon(menu)"
                                      class="d-inline-block ml-1 px-1"
                                      color="purple"
                                      small
                                      style="min-width: 0px;"
                                      text
                                    >
                                      <v-icon>
                                        redeem
                                      </v-icon>
                                    </v-btn>
                                  </v-list-item-action-text>
                                </v-list-item-action>
                              </v-list-item>

                              <v-divider
                                v-if="!menuItemHasExtraCharges(menu)"
                                :key="`cart-${payment.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-item-divider-${menu.RowId}-hash-${menu.HashId}`"
                                :class="menuItemHasExtraCharges(menu) ? 'mb-0' : 'mb-1'"
                              />

                              <!-- Menu Item Extra Charges -->
                              <template v-for="(charge, chargeIdx) in getMenuItemExtraCharges(menu)">
                                <v-divider
                                  v-if="chargeIdx > 0"
                                  :key="`cart-${payment.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-item-${menu.RowId}-hash-${menu.HashId}-charge-${charge.Id}-divider`"
                                />

                                <v-list-item
                                  :key="`cart-${payment.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-item-${menu.RowId}-hash-${menu.HashId}-charge-${charge.Id}`"
                                  :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                                  style="min-height: 30px !important; height: 30px;"
                                >
                                  <v-list-item-content class="py-0">
                                    <span
                                      :style="`background-color: #${getCategoryColor(category.CategoryId) || '333333'};`"
                                      style="position: absolute; width:5px; height: 100%; margin-left: -16px; padding: 0; margin-right: 16px; top: 0px;"
                                    />

                                    <v-list-item-title>
                                      <span class="orange--text">{{ charge.Quantity }} x </span>
                                      <span class="grey--text text--darken-1">
                                        {{ charge.Title }}
                                      </span>
                                    </v-list-item-title>
                                  </v-list-item-content>

                                  <v-list-item-action-text
                                    class="text-right"
                                    style="min-width: 50px;"
                                  >
                                    <span class="grey--text text--darken-1">
                                      +{{ charge.AmountFormatted | currency }}
                                    </span>
                                  </v-list-item-action-text>
                                </v-list-item>
                              </template>

                              <v-divider
                                v-if="menuItemHasExtraCharges(menu)"
                                :key="`cart-${payment.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-item-charge-divider-${menu.RowId}-hash-${menu.HashId}`"
                                class="mb-1"
                              />
                            </template>
                          </v-list>
                        </template>
                      </template>
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>
            </v-row>
          </v-container>
        </template>
      </v-col>
    </v-row>


    <confirm-dialog
      :html-content="$t('Restaurant.Tables.Dialog.ViewTable.Dialog.ConfirmRevertPaymentItems.Content')"
      :html-title="$t('Restaurant.Tables.Dialog.ViewTable.Dialog.ConfirmRevertPaymentItems.Title')"
      :info-bar="true"
      :info-bar-body="$t('Restaurant.Tables.Dialog.ViewTable.Dialog.ConfirmRevertPaymentItems.Notification')"
      :visible.sync="confirmDialogRevertPaymentItemsVisible"
      :width="560"
      no-button-class="v-btn--outline green--text"
      title-class="indigo white--text lighten-0"
      yes-button-class="indigo white--text"
      @confirm-dialog-yes="onConfirmRevertPaymentItems"
    />
  </v-container>
</template>

<script>
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'
import TableStatus from '@/mixins/tables/tableStatus'
import PaymentData from '@/mixins/tables/paymentData'
import Translatable from '@/mixins/translatable'
import PosCatalog from '@/mixins/pos/posCatalog'
import PaymentType from '@/mixins/orders/paymentType'
import TableCommon from '@/mixins/tables/tableCommon'
import {formatUnitQuantity} from '@/lib/currency/currency'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum.js'
import IssueReceiptDocumentButton from '@/components/common/receipt/IssueReceiptDocumentButton.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import EmailNotifyFromEnum from '@/api/Enums/EmailNotifyFromEnum.js'
import {sortBy} from '@/lib/helper/helper.js'

export default {
  name      : 'TablePaymentsList',
  components: {ConfirmDialog, IssueReceiptDocumentButton},
  directives: {},
  mixins    : [Auth, AppData, Translatable, TableStatus, TableCommon, PaymentData, PosCatalog, PaymentType],
  props     : {
    viewData: {
      type   : Object,
      default: undefined
    },
    table: {
      type   : Object,
      default: undefined
    }
  },
  dataStore: {},
  data() {
    return {
      confirmDialogRevertPaymentItemsVisible: false,
      generatePaymentReceiptLoading         : false
    }
  },
  computed: {
    EmailNotifyFromEnum() {
      return EmailNotifyFromEnum
    },
    FiscalDocTypeEnum() {
      return FiscalDocTypeEnum
    }
  },
  watch: {},
  beforeCreate() {
  },
  created() {
    this.$bus.$on(window.SocketCommand.Receipt.GenerateFromCartTablePaymentTimestamp, this.onGeneratePaymentReceiptResponse)
    this.$bus.$on('invoice-company:canceled', this.onInvoiceCompanyCanceled)
    this.$bus.$on(window.SocketCommand.Table.Show, this.onTableUpdate)
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Receipt.GenerateFromCartTablePaymentTimestamp, this.onGeneratePaymentReceiptResponse)
    this.$bus.$off('invoice-company:canceled', this.onInvoiceCompanyCanceled)
    this.$bus.$off(window.SocketCommand.Table.Show, this.onTableUpdate)
  },
  destroyed() {
  },
  methods: {
    formatUnitQuantity,

    onRevertPaymentItems(payment) {
      if (!payment || payment?.loading) return

      this.selectedPayment = payment
      this.confirmDialogRevertPaymentItemsVisible = true
    },

    onConfirmRevertPaymentItems() {
      const payload = {
        CartId          : this.table?.CartId || null,
        ReceiptId       : this.paymentFiscalDocument(this.selectedPayment)?.OrderDocument?.Id || null,
        TableId         : this.table?.Id || null,
        PaymentTimestamp: this.selectedPayment?.Timestamp || null
      }

      if (!this.selectedPayment || !payload.TableId || !payload.CartId || !payload.PaymentTimestamp) return

      this.$set(this.selectedPayment, 'loading', true)

      window.callAS(window.SocketCommand.Table.Cart.RevertPaymentItems, payload)
    },

    onInvoiceCompanyCanceled() {
      this.generatePaymentReceiptLoading = false
    },

    paymentFiscalDocument(payment) {
      const paymentItems = payment?.Items?.map(paymentItem => paymentItem.RowId) || []
      const documents = this.viewData?.Receipts?.filter(document => FiscalDocTypeEnum.isReceipt(document.Type) || FiscalDocTypeEnum.isInvoice(document.Type)) || []
      const document = sortBy(documents, 'OrderId', 'desc').find(document => document.Items.every(documentItem => paymentItems.includes(documentItem)))
      const orderDocument = this.viewData?.OrderReceipts?.find(orderDocument => !orderDocument.IsCredit && orderDocument.CanCancel && orderDocument.OrderId === document?.OrderId && !orderDocument?.ErrorCode)

      return {Document: document, OrderDocument: orderDocument}
    },
    paymentHasFiscalDocument(payment) {
      return !!this.paymentFiscalDocument(payment)?.Document && !!this.paymentFiscalDocument(payment)?.OrderDocument
    },

    paymentReceipt(payment) {
      const paymentItems = payment?.Items?.map(paymentItem => paymentItem.RowId) || []
      const receipts = this.viewData?.Receipts?.filter(receipt => FiscalDocTypeEnum.isReceipt(receipt.Type)) || []
      const receipt = sortBy(receipts, 'OrderId', 'desc').find(receipt => receipt.Items.every(receiptItem => paymentItems.includes(receiptItem)))
      const orderReceipt = this.viewData?.OrderReceipts?.find(orderDocument => !orderDocument.IsCredit && orderDocument.CanCancel && orderDocument.OrderId === document?.OrderId && !orderDocument?.ErrorCode)
      return {Receipt: receipt, OrderReceipt: orderReceipt}

    },
    paymentHasReceipt(payment) {
      return !!this.paymentReceipt(payment)?.Receipt && !!this.paymentReceipt(payment)?.OrderReceipt
    },

    paymentInvoice(payment) {
      const paymentItems = payment?.Items?.map(paymentItem => paymentItem.RowId) || []
      const invoices = this.viewData?.Receipts?.filter(invoice => FiscalDocTypeEnum.isInvoice(invoice.Type)) || []
      const invoice = sortBy(invoices, 'OrderId', 'desc').find(invoice => invoice.Items.every(invoiceItem => paymentItems.includes(invoiceItem)))
      const invoiceReceipt = this.viewData?.OrderReceipts?.find(orderDocument => !orderDocument.IsCredit && orderDocument.CanCancel && orderDocument.OrderId === document?.OrderId && !orderDocument?.ErrorCode)
      return {Invoice: invoice, InvoiceReceipt: invoiceReceipt}
    },
    paymentHasInvoice(payment) {
      return !!this.paymentInvoice(payment)?.Invoice
    },

    generatePaymentReceipt(payment, docType) {
      if (!this.table || !payment) return

      this.generatePaymentReceiptLoading = true

      const payload = {
        TableId    : this.table?.Id,
        CartId     : this.table?.CartId,
        Company    : null,
        ReceiptType: docType || FiscalDocTypeEnum.RECEIPT,
        Timestamp  : payment?.Timestamp
      }

      this.$bus.$emit('fiscal-document:create', payload, docType, null, window.SocketCommand.Receipt.GenerateFromCartTablePaymentTimestamp, window.SocketCommand.Receipt.GenerateFromCartTablePaymentTimestamp)
    },

    onGeneratePaymentReceiptResponse(data) {
      if (data.status === 'success') return

      this.$bus.$emit('app-show-notification', {
        body   : this.$t('Common.Error.Generic'),
        type   : 'error',
        icon   : 'warning',
        timeout: 3000
      })
      this.generatePaymentReceiptLoading = false
    },

    onTableUpdate() {
      this.generatePaymentReceiptLoading = false
    }
  }
}
</script>

<style scoped>

</style>
